import type { CSSProperties, ReactNode } from "react";
import { DESKTOP, MOBILE, Num, type Marker } from "@/components/home-a/StorePage";

/**
 * HALDEN, the demo lead-generation page: a small accounting firm asking for a
 * quote. It is the lead-gen twin of the KYMA store, drawn the same way (fixed
 * base size, absolute positions, scaled into place) so the solution pages can
 * show a form, its markers and its attention map like a real capture.
 *
 * `after` is the page with the report's top changes applied: three fields
 * instead of eight, a button that says what you get, and proof beside the form.
 * The colours are the firm's own, not EyeCaptain's, so they are literals.
 */

const L = {
  bg: "#f5f6f8",
  ink: "#14213d",
  muted: "#667085",
  line: "#d7dbe2",
  field: "#ffffff",
  cta: "#9aa4b5",
  ctaAfter: "#14213d",
  accent: "#b45309", // amber dark enough to read on the page (4.6 : 1)
};

function Abs({ x, y, w, h, style, children }: { x: number; y: number; w: number; h?: number; style?: CSSProperties; children?: ReactNode }) {
  return <div style={{ position: "absolute", left: x, top: y, width: w, height: h, ...style }}>{children}</div>;
}

function Field({ x, y, w, label, h = 40, select = false }: { x: number; y: number; w: number; label: string; h?: number; select?: boolean }) {
  return (
    <>
      <Abs x={x} y={y} w={w} h={14} style={{ fontSize: 11.5, color: L.muted }}>{label}</Abs>
      <Abs x={x} y={y + 18} w={w} h={h} style={{ background: L.field, border: `1px solid ${L.line}`, borderRadius: 6, boxSizing: "border-box" }}>
        {select && <div style={{ position: "absolute", right: 12, top: h / 2 - 3, width: 7, height: 7, borderRight: `1.5px solid ${L.muted}`, borderBottom: `1.5px solid ${L.muted}`, transform: "rotate(45deg)" }} />}
      </Abs>
    </>
  );
}

export type LeadVariant = { after?: boolean };

export const LEAD_DESKTOP_MARKERS: Marker[] = [
  { n: 1, x: 60, y: 150, w: 560, h: 150 },
  { n: 2, x: 700, y: 150, w: 440, h: 470 },
  { n: 3, x: 724, y: 640, w: 392, h: 50 },
  { n: 4, x: 60, y: 560, w: 560, h: 60 },
];

export const LEAD_MOBILE_MARKERS: Marker[] = [
  { n: 1, x: 16, y: 84, w: 358, h: 110 },
  { n: 2, x: 16, y: 262, w: 358, h: 470 },
  { n: 3, x: 32, y: 744, w: 326, h: 48 },
];

export function DesktopLead({ after }: LeadVariant) {
  return (
    <>
      <Abs x={0} y={0} w={1200} h={72} style={{ background: "#fff", borderBottom: `1px solid ${L.line}`, boxSizing: "border-box" }} />
      <Abs x={60} y={24} w={200} h={24} style={{ fontSize: 20, fontWeight: 800, letterSpacing: "0.18em", color: L.ink }}>HALDEN</Abs>
      <Abs x={470} y={27} w={380} h={18} style={{ display: "flex", gap: 30, fontSize: 14, color: L.ink }}>
        <span>Services</span><span>Pricing</span><span>About</span><span>Blog</span>
      </Abs>
      <Abs x={1010} y={18} w={130} h={36} style={{ border: `1.5px solid ${L.ink}`, borderRadius: 6, boxSizing: "border-box", fontSize: 13.5, fontWeight: 600, color: L.ink, display: "flex", alignItems: "center", justifyContent: "center" }}>
        Call us
      </Abs>

      <Abs x={60} y={118} w={560} h={16} style={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.14em", color: L.accent }}>ACCOUNTING FIRM · SINCE 2009</Abs>
      <Abs x={60} y={150} w={560} h={150} style={{ fontSize: 44, lineHeight: 1.12, fontWeight: 700, color: L.ink, letterSpacing: "-0.02em" }}>
        {after ? "Bookkeeping for small businesses, done every month" : "Innovative financial solutions for your future"}
      </Abs>
      <Abs x={60} y={318} w={520} h={60} style={{ fontSize: 17, lineHeight: 1.55, color: L.muted }}>
        {after ? "A fixed monthly fee, a named accountant, and your books closed by the 5th." : "We are a team of dedicated professionals committed to excellence in everything we do."}
      </Abs>
      {["Named accountant", "Fixed monthly fee", "Books closed by the 5th"].map((t, i) => (
        <Abs key={t} x={60} y={404 + i * 34} w={400} h={22} style={{ fontSize: 15, color: L.ink, display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ width: 18, height: 18, borderRadius: 99, background: after ? L.ink : L.line, display: "inline-block" }} />
          {t}
        </Abs>
      ))}
      <Abs x={60} y={560} w={560} h={60} style={{ borderTop: `1px solid ${L.line}`, boxSizing: "border-box", paddingTop: 18, display: "flex", gap: 36, fontSize: 14, fontWeight: 700, color: "#aab1bd", letterSpacing: "0.08em" }}>
        {after ? (
          <span style={{ color: L.ink, letterSpacing: 0, fontWeight: 600 }}>★ 4.9 from 212 client reviews · 380 businesses served</span>
        ) : (
          <>
            <span>ACME</span><span>NORTH</span><span>BRIGHT</span><span>OAKS</span>
          </>
        )}
      </Abs>

      <Abs x={700} y={118} w={440} h={600} style={{ background: "#fff", border: `1px solid ${L.line}`, borderRadius: 12, boxSizing: "border-box", boxShadow: "0 20px 50px rgba(20,33,61,0.08)" }} />
      <Abs x={724} y={138} w={392} h={24} style={{ fontSize: 19, fontWeight: 700, color: L.ink }}>{after ? "Get a fixed quote in one day" : "Contact us"}</Abs>
      {after ? (
        <>
          <Field x={724} y={184} w={392} label="Your name" h={44} />
          <Field x={724} y={260} w={392} label="Work email" h={44} />
          <Field x={724} y={336} w={392} label="Company size" h={44} select />
          <Abs x={724} y={420} w={392} h={120} style={{ background: "#f0f3f8", borderRadius: 8, padding: 16, boxSizing: "border-box", fontSize: 13, lineHeight: 1.55, color: L.ink }}>
            "We switched in March. The books have closed on time every month since."
            <div style={{ marginTop: 8, fontSize: 12, color: L.muted }}>Maria K., owner of a design studio</div>
          </Abs>
        </>
      ) : (
        <>
          <Field x={724} y={176} w={188} label="First name" />
          <Field x={928} y={176} w={188} label="Last name" />
          <Field x={724} y={244} w={392} label="Email" />
          <Field x={724} y={312} w={188} label="Phone" />
          <Field x={928} y={312} w={188} label="Company" />
          <Field x={724} y={380} w={188} label="Annual revenue" select />
          <Field x={928} y={380} w={188} label="Employees" select />
          <Field x={724} y={448} w={392} label="How did you hear about us?" select />
          <Field x={724} y={516} w={392} label="Message" h={84} />
        </>
      )}
      <Abs
        x={724}
        y={640}
        w={392}
        h={50}
        style={{ background: after ? L.ctaAfter : L.cta, color: "#fff", borderRadius: 8, fontSize: 15.5, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center" }}
      >
        {after ? "Get my free quote" : "Submit"}
      </Abs>
      {after && (
        <Abs x={724} y={698} w={392} h={16} style={{ fontSize: 12, color: L.muted, textAlign: "center" }}>Reply within one business day · No commitment</Abs>
      )}
    </>
  );
}

export function MobileLead({ after }: LeadVariant) {
  return (
    <>
      <Abs x={0} y={0} w={390} h={60} style={{ background: "#fff", borderBottom: `1px solid ${L.line}`, boxSizing: "border-box" }} />
      <Abs x={16} y={20} w={140} h={22} style={{ fontSize: 17, fontWeight: 800, letterSpacing: "0.18em", color: L.ink }}>HALDEN</Abs>
      <Abs x={352} y={22} w={20} h={16}>
        {[0, 1, 2].map((i) => (
          <div key={i} style={{ height: 1.8, background: L.ink, marginBottom: i < 2 ? 5 : 0 }} />
        ))}
      </Abs>
      <Abs x={16} y={84} w={358} h={110} style={{ fontSize: 28, lineHeight: 1.15, fontWeight: 700, color: L.ink, letterSpacing: "-0.02em" }}>
        {after ? "Bookkeeping for small businesses, done monthly" : "Innovative financial solutions for your future"}
      </Abs>
      <Abs x={16} y={200} w={358} h={48} style={{ fontSize: 14, lineHeight: 1.5, color: L.muted }}>
        {after ? "Fixed monthly fee. Named accountant. Books closed by the 5th." : "A team of dedicated professionals committed to excellence."}
      </Abs>
      <Abs x={16} y={262} w={358} h={560} style={{ background: "#fff", border: `1px solid ${L.line}`, borderRadius: 12, boxSizing: "border-box" }} />
      {after ? (
        <>
          <Abs x={32} y={280} w={326} h={22} style={{ fontSize: 17, fontWeight: 700, color: L.ink }}>Get a fixed quote in one day</Abs>
          <Field x={32} y={318} w={326} label="Your name" h={46} />
          <Field x={32} y={396} w={326} label="Work email" h={46} />
          <Field x={32} y={474} w={326} label="Company size" h={46} select />
          <Abs x={32} y={556} w={326} h={70} style={{ background: "#f0f3f8", borderRadius: 8, padding: 12, boxSizing: "border-box", fontSize: 12.5, lineHeight: 1.5, color: L.ink }}>
            ★ 4.9 from 212 client reviews · 380 businesses served
          </Abs>
          <Abs x={32} y={644} w={326} h={48} style={{ background: L.ctaAfter, color: "#fff", borderRadius: 8, fontSize: 15, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center" }}>
            Get my free quote
          </Abs>
          <Abs x={32} y={700} w={326} h={16} style={{ fontSize: 11.5, color: L.muted, textAlign: "center" }}>Reply within one business day</Abs>
        </>
      ) : (
        <>
          <Abs x={32} y={280} w={326} h={22} style={{ fontSize: 17, fontWeight: 700, color: L.ink }}>Contact us</Abs>
          <Field x={32} y={314} w={155} label="First name" h={38} />
          <Field x={203} y={314} w={155} label="Last name" h={38} />
          <Field x={32} y={378} w={326} label="Email" h={38} />
          <Field x={32} y={442} w={155} label="Phone" h={38} />
          <Field x={203} y={442} w={155} label="Company" h={38} />
          <Field x={32} y={506} w={326} label="Annual revenue" h={38} select />
          <Field x={32} y={570} w={326} label="How did you hear about us?" h={38} select />
          <Field x={32} y={634} w={326} label="Message" h={70} />
          <Abs x={32} y={744} w={326} h={48} style={{ background: L.cta, color: "#fff", borderRadius: 8, fontSize: 15, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center" }}>
            Submit
          </Abs>
        </>
      )}
    </>
  );
}

const LEAD_HEAT = {
  mobile: [
    [190, 130, 150, 0.9],
    [110, 220, 90, 0.45],
    [190, 330, 120, 0.5],
    [190, 770, 80, 0.12],
  ],
  desktop: [
    [320, 210, 230, 0.95],
    [1010, 36, 70, 0.55],
    [900, 250, 180, 0.5],
    [920, 665, 110, 0.14],
  ],
} as const;

/** After the changes: the dark button and the proof pull the eye to the form. */
const LEAD_HEAT_AFTER = {
  mobile: [
    [190, 130, 130, 0.7],
    [190, 590, 90, 0.5],
    [190, 668, 100, 0.85],
  ],
  desktop: [
    [320, 210, 210, 0.75],
    [920, 665, 120, 0.9],
    [360, 590, 130, 0.45],
    [920, 470, 140, 0.4],
  ],
} as const;

/** A capture of the HALDEN page, scaled into place. Same contract as the store's Capture. */
export function LeadCapture({
  kind = "desktop",
  scale,
  cropTop = 0,
  cropH,
  markers,
  showNumbers = true,
  heat = false,
  variant,
  radius = 10,
  frame = true,
  className,
  style,
  label,
}: {
  kind?: "mobile" | "desktop";
  scale: number;
  cropTop?: number;
  cropH?: number;
  markers?: Marker[];
  showNumbers?: boolean;
  heat?: boolean;
  variant?: LeadVariant;
  radius?: number;
  frame?: boolean;
  className?: string;
  style?: CSSProperties;
  label?: string;
}) {
  const base = kind === "mobile" ? MOBILE : DESKTOP;
  const h = cropH ?? base.h - cropTop;
  const k = 1 / scale;
  return (
    <div
      role={label ? "img" : undefined}
      aria-label={label}
      aria-hidden={label ? undefined : true}
      className={className}
      style={{
        position: "relative",
        flexShrink: 0,
        width: base.w * scale,
        height: h * scale,
        overflow: "hidden",
        borderRadius: radius,
        background: L.bg,
        boxShadow: frame ? "var(--shadow-frame), 0 0 0 1px hsl(var(--foreground) / 0.06)" : undefined,
        ...style,
      }}
    >
      <div
        style={{
          position: "absolute",
          left: 0,
          top: -cropTop * scale,
          width: base.w,
          height: base.h,
          transform: `scale(${scale})`,
          transformOrigin: "0 0",
          fontFamily: "'Manrope', system-ui, sans-serif",
          background: L.bg,
        }}
      >
        {kind === "mobile" ? <MobileLead {...variant} /> : <DesktopLead {...variant} />}
        {heat && (
          <>
            <div style={{ position: "absolute", inset: 0, background: "rgba(5,9,7,0.6)" }} />
            {(variant?.after ? LEAD_HEAT_AFTER : LEAD_HEAT)[kind].map(([cx, cy, r, a]) => (
              <div
                key={`${cx}-${cy}`}
                style={{
                  position: "absolute",
                  left: cx - r,
                  top: cy - r,
                  width: 2 * r,
                  height: 2 * r,
                  borderRadius: 999,
                  mixBlendMode: "screen",
                  background: `radial-gradient(circle, rgba(200,255,228,${a}) 0%, rgba(111,214,168,${a * 0.7}) 22%, rgba(62,194,140,${a * 0.28}) 50%, transparent 72%)`,
                }}
              />
            ))}
          </>
        )}
        {markers?.map((m) => (
          <div key={m.n}>
            <div
              style={{
                position: "absolute",
                left: m.x,
                top: m.y,
                width: m.w,
                height: m.h,
                boxSizing: "border-box",
                border: `${2 * k}px solid hsl(var(--ec-deep))`,
                borderRadius: 5 * k,
                background: "rgba(62,194,140,0.10)",
              }}
            />
            {showNumbers && (
              <div style={{ position: "absolute", left: m.x - 22 * k * 0.35, top: m.y - 22 * k * 0.4, transform: `scale(${k})`, transformOrigin: "0 0" }}>
                <Num n={m.n} onImage />
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
